import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { unByKey } from 'ol/Observable';

import { useProject } from '@contexts/Project.context';
import { calculateScaleBar } from '@utils/map/scaleBar';
import { SELECTOR_POSITION } from '@routes/userRoutes/projects/singleProject/components/layers/baseMap/constants';
import { PROJECT_MODE } from '@utils/constants';

const MAX_BAR_WIDTH = 150; // CSS pixels

const ScaleBarContainer = styled.div`
	position: absolute;
	bottom: ${({ $bottom }) => $bottom}px;
	right: ${SELECTOR_POSITION.right + 40 + 12}px; /* base map button width + spacing */
	z-index: 1;
	pointer-events: none;
	background: ${({ theme }) => theme.colors.background || '#1a1a2e'};
	border-radius: 4px;
	padding: 6px 10px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 40px; /* match base map button height */
	gap: 3px;
	transition: 0.2s ease-in-out;
`;

const Bar = styled.div`
	height: 4px;
	background: ${({ theme }) => theme.colors.textColor || '#c0c0c0'};
	position: relative;
	border-radius: 1px;

	/* Left tick */
	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: -2px;
		bottom: -2px;
		width: 1.5px;
		background: ${({ theme }) => theme.colors.textColor || '#c0c0c0'};
	}

	/* Right tick */
	&::after {
		content: '';
		position: absolute;
		right: 0;
		top: -2px;
		bottom: -2px;
		width: 1.5px;
		background: ${({ theme }) => theme.colors.textColor || '#c0c0c0'};
	}
`;

const Label = styled.span`
	color: ${({ theme }) => theme.colors.textColor || '#c0c0c0'};
	font-size: 11px;
	font-weight: bold;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	line-height: 1;
`;

/**
 * MapScaleBar — displays a scale bar on the map that updates on zoom/pan.
 *
 * Uses the same `calculateScaleBar()` function as the export `drawScaleBar`,
 * ensuring identical values and distance representation between the live map
 * and exported reports.
 *
 * Positioned bottom-right (to the left of the base map selector),
 * styled to match the export scale bar appearance.
 */
const MapScaleBar = () => {
	const { mapObject, projectMode, singleImageSliderOpen, singleImageFeatures } = useProject();
	const [scaleInfo, setScaleInfo] = useState(null);

	const updateScale = useCallback(() => {
		if (!mapObject) return;
		const info = calculateScaleBar(mapObject, MAX_BAR_WIDTH);
		setScaleInfo(info);
	}, [mapObject]);

	useEffect(() => {
		if (!mapObject) return;

		// Initial calculation
		updateScale();

		// Update on zoom/pan
		// change:resolution → smooth updates during zoom animation
		// moveend → catches pan-only movements that don't change resolution
		const view = mapObject.getView();
		const resolutionKey = view.on('change:resolution', updateScale);
		const moveKey = mapObject.on('moveend', updateScale);

		return () => {
			unByKey(resolutionKey);
			unByKey(moveKey);
		};
	}, [mapObject, updateScale]);

	if (!scaleInfo) return null;

	// Match BaseMapSelector positioning: move up when single image slider is open
	const isSliderVisible =
		projectMode === PROJECT_MODE.SINGLE_IMAGE &&
		singleImageSliderOpen &&
		singleImageFeatures?.length > 0;

	const bottomOffset = isSliderVisible ? SELECTOR_POSITION.withSlider : SELECTOR_POSITION.default;

	return (
		<ScaleBarContainer
			$bottom={bottomOffset}
			aria-label={`Scale: ${scaleInfo.label}`}
			role="img">
			<Bar style={{ width: `${scaleInfo.pixelWidth}px` }} />
			<Label>{scaleInfo.label}</Label>
		</ScaleBarContainer>
	);
};

export default MapScaleBar;
